// src/pages/ShortCodeRedirector.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Container, Spinner, Card, CardBody } from "reactstrap";
import { getShortCodeData } from "../../helpers/eirna_backend_helper";
import { getAuthToken, getLoggedinUser } from "../../helpers/api_helper";

const ShortCodeRedirector = () => {
  const { shortCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Create isAuthenticated function using your existing api_helper functions
  const isAuthenticated = () => {
    const token = getAuthToken();
    const user = getLoggedinUser();
    return !!(token && user);
  };

  const getMockShortCodeData = async (shortCode) => {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    return {
      cov_check: false,
      file_list: [3318, 3319, 3320, 3321, 3322, 3323],
      hili_start: "50",
      hili_stop: "120",
      maxread: 150,
      minread: 20,
      organism: "homo_sapiens",
      plot_url: "/interactive_plot/",
      primetype: "fiveprime",
      project_id: "GYLWrxBWUU",
      readscore: "0",
      seqhili: "",
      transcript: "ENST00000316292",
      transcriptome: "homo_sapiens.Gencode_v41.appris",
    };
  };

  useEffect(() => {
    const handleShortCode = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if user is authenticated using api_helper functions
        if (!isAuthenticated()) {
          // Redirect to login if not authenticated
          navigate("/login", {
            state: {
              message: "Please log in to view this shared plot",
              returnUrl: location.pathname,
            },
          });
          return;
        }

        // User is authenticated - fetch short code data and navigate
        // const response = await getMockShortCodeData(shortCode);
        const response = await getShortCodeData(shortCode);

        if (response && response.plot_url && response.project_id) {
          let plotUrl = `/projects/${response.project_id}${response.plot_url}`;

          // Special case for genome browser short codes, need to include url params
          if (
            response.plot_url === "/single_genomebrowser/" ||
            response.plot_url === "/comparison_genomebrowser/"
          ) {
            plotUrl += "?"
            plotUrl += `chrinput=${response.chrinput}`
            plotUrl += `&ambig=${response.ambig}`
            plotUrl += `&reverse=${response.reverse}`
            // Only one of file_dict or file_list will be present depending on plot type

            // file_dict is for comparison genome browser
            if (response.file_dict){
              plotUrl += `&file_dict=${response.file_dict}`
            }

            // file_string is for single genome browser
            if (response.file_string){
              plotUrl += `&file_list=${response.file_string}`
            }
          }

          // UPDATED: Include the original short code in state for the new API structure
          navigate(plotUrl, {
            state: {
              shortCodePayload: response,
              isFromShortCode: true,
              shortCode: shortCode,
            },
            replace: true,
          });
        } else {
          setError("Invalid short code or plot data not found.");
        }
      } catch (err) {
        console.error("Short code fetch error:", err);

        if (err.response?.status === 401 || err.response?.status === 403) {
          navigate("/login", {
            state: {
              message: "Your session has expired. Please log in again.",
              returnUrl: location.pathname,
            },
          });
        } else if (err.response?.status === 404) {
          setError(
            "Short code not found. Please check the link and try again."
          );
        } else {
          setError(
            "Failed to load plot data. Please check the short code and your connection."
          );
        }
      } finally {
        setLoading(false);
      }
    };

    if (shortCode) {
      handleShortCode();
    } else {
      setError("No short code provided in the URL.");
      setLoading(false);
    }
  }, [shortCode, navigate, location.pathname]);

  // Loading state
  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "60vh" }}
          >
            <div className="text-center">
              <Spinner
                color="success"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p className="mt-3 text-muted">Loading shared plot...</p>
              <small className="text-muted">
                Short code: <code>{shortCode}</code>
              </small>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "60vh" }}
          >
            <Card style={{ maxWidth: "500px", width: "100%" }}>
              <CardBody className="text-center p-4">
                <i
                  className="ri-error-warning-line text-danger mb-3"
                  style={{ fontSize: "3rem" }}
                ></i>
                <h5 className="text-danger mb-3">Error Loading Plot</h5>
                <p className="text-muted mb-3">{error}</p>
                <div className="d-flex gap-2 justify-content-center flex-wrap">
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => window.location.reload()}
                  >
                    <i className="ri-refresh-line me-1"></i>
                    Try Again
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate("/dashboard")}
                  >
                    <i className="ri-dashboard-line me-1"></i>
                    Go to Dashboard
                  </button>
                </div>
                <div className="mt-3">
                  <small className="text-muted">
                    Short code: <code>{shortCode}</code>
                  </small>
                </div>
              </CardBody>
            </Card>
          </div>
        </Container>
      </div>
    );
  }

  return null;
};

export default ShortCodeRedirector;
